// Global Instructions Rule Applied!
import React from 'react'
import { InputNumber as AntInputNumber } from 'antd'
import { formatWithPolicy, thousandsParser } from '../utils/inputNumberFormat.ts'

/**
 * Enhanced InputNumber component with automatic formatting and currency support
 *
 * @param {{
 *   isCurrency?: boolean,
 *   currencySymbol?: string,
 *   enableFormatting?: boolean,
 *   formatter?: (value: string | number | undefined, info?: { userTyping: boolean, input: string }) => string,
 *   parser?: (value: string | undefined) => string | number,
 *   onBlur?: React.FocusEventHandler<HTMLInputElement>,
 *   [antProp: string]: any
 * }} props
 *   `isCurrency` shows `currencySymbol` (default '$') as addonBefore; `enableFormatting` (default true) applies the
 *   house thousands formatter/parser unless a custom `formatter` / `parser` is given. Everything else is forwarded
 *   to the Ant Design InputNumber.
 */
const InputNumber = ({
  isCurrency = false,
  currencySymbol = '$',
  enableFormatting = true,
  formatter,
  parser,
  ...props
}) => {
  // Thousands separators on the integer part only (the old whole-string regex
  // also comma-grouped the decimals: 1.2345 -> "1.2,345"), applied live while
  // typing via AntD's userTyping flag — shared house impl in inputNumberFormat.
  const defaultFormatter = (value, info) => formatWithPolicy(value, null, Boolean(info?.userTyping))

  const defaultParser = (value) => thousandsParser(value)

  // Optimized real-time input validation - prevents invalid characters with minimal delay
  const handleKeyDown = (event) => {
    const { key, ctrlKey, altKey, metaKey } = event

    // Allow control keys and shortcuts
    if (ctrlKey || altKey || metaKey) return

    // Allow navigation and editing keys (more comprehensive list)
    if (key.length > 1) return // This covers all special keys like 'Backspace', 'ArrowLeft', etc.

    // Only prevent single character keys that aren't digits, comma, period, or minus
    if (!/[0-9,.-]/.test(key)) {
      event.preventDefault()
      return false
    }

    // Call original onKeyDown if provided
    props.onKeyDown?.(event)
  }

  // Determine which formatter and parser to use
  const finalFormatter = formatter || (enableFormatting ? defaultFormatter : undefined)
  const finalParser = parser || (enableFormatting ? defaultParser : undefined)

  return (
    <AntInputNumber
      {...props}
      addonBefore={isCurrency ? currencySymbol : props.addonBefore}
      formatter={finalFormatter}
      parser={finalParser}
      onKeyDown={handleKeyDown}
    />
  )
}

export default InputNumber
